import React, { useMemo } from "react";
import DOMPurify from "dompurify";

const SAFE_CONTAINER_TAGS = new Set(["div", "p", "span"]);

const SANITIZE_CONFIG = Object.freeze({
  ALLOWED_TAGS: [
    "p", "br", "strong", "b", "em", "i", "u", "s",
    "ul", "ol", "li", "blockquote",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "div", "span", "pre", "code", "hr",
    "table", "caption", "thead", "tbody", "tfoot", "tr", "th", "td",
    "a",
  ],
  ALLOWED_ATTR: [
    "class", "href", "title", "colspan", "rowspan", "scope",
  ],
  ALLOW_ARIA_ATTR: false,
  ALLOW_DATA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  FORBID_TAGS: [
    "script", "style", "iframe", "object", "embed", "svg", "math",
    "form", "input", "button", "textarea", "select", "option",
    "template", "meta", "link", "base", "img", "video", "audio",
    "source", "canvas",
  ],
  FORBID_ATTR: [
    "style", "src", "srcset", "srcdoc", "formaction", "xlink:href",
  ],
  RETURN_TRUSTED_TYPE: false,
});

export const sanitizeSafeHtml = (html) => {
  if (typeof html !== "string") return "";
  return DOMPurify.sanitize(html, SANITIZE_CONFIG);
};

/**
 * The application's only reviewed raw-HTML rendering boundary.
 *
 * Keep the allowlist above narrow. Callers may choose only a simple container
 * element; all content is sanitized at render time so legacy records are also
 * protected.
 */
const SafeHtml = React.forwardRef(({
  as = "div",
  html = "",
  ...containerProps
}, ref) => {
  const sanitizedHtml = useMemo(() => sanitizeSafeHtml(html), [html]);
  const Container = SAFE_CONTAINER_TAGS.has(as) ? as : "div";

  return (
    <Container
      {...containerProps}
      ref={ref}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
});

SafeHtml.displayName = "SafeHtml";

export default SafeHtml;
